import React, { useState } from 'react';
import {
  EllipsisVerticalIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  BanknotesIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ClockIcon,
  BoltIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { Client } from '../../types';
import { applyDominicanIDMask } from '../../utils/validation';
import { deriveClientStatus } from './clientStatus';
import { ClientStatusBadge } from './ClientStatusBadge';
import type { ClientActionGroupProps } from './ClientActionGroup';
import { BottomSheet } from '../ui/BottomSheet';

function getInitials(name: string, lastname: string) {
  const a = (name || '').trim().charAt(0);
  const b = (lastname || '').trim().charAt(0);
  return `${a}${b}`.toUpperCase() || '—';
}

function avatarTone(seed: string): string {
  const tones = [
    'bg-primary-100 text-primary-800 ring-primary-200/80',
    'bg-slate-200 text-slate-800 ring-slate-300/80',
    'bg-teal-100 text-teal-900 ring-teal-200/80',
    'bg-amber-100 text-amber-900 ring-amber-200/80',
    'bg-sky-100 text-sky-900 ring-sky-200/80',
  ];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = seed.charCodeAt(i) + ((h << 5) - h);
  return tones[Math.abs(h) % tones.length];
}

function planName(client: Client): string {
  const plans = client.plans || [];
  if (!plans.length) return 'Sin plan';
  const primary = plans[0] as { planTemplate?: { name?: string }; name?: string };
  return (primary.planTemplate as { name?: string } | undefined)?.name || primary.name || 'Plan';
}

export interface ClientMobileCardProps {
  client: Client;
  handlers: ClientActionGroupProps;
  onOpenDetail: () => void;
}

export const ClientMobileCard: React.FC<ClientMobileCardProps> = ({
  client,
  handlers,
  onOpenDetail,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const status = deriveClientStatus(client);
  const gremio = (client.gremio || '').trim();
  const famCount = client.familyMembers?.length ?? 0;
  const cedulaDisplay =
    applyDominicanIDMask(String(client.cedula || '').replace(/\D/g, '')) || client.cedula;

  const run = (fn: () => void) => {
    setMenuOpen(false);
    fn();
  };

  return (
    <>
      <article
        className={clsx(
          'rounded-2xl border border-[#E5EAF1] bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]',
          'transition duration-150 active:scale-[0.99] active:bg-slate-50/80'
        )}
      >
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onOpenDetail}
            className="flex min-w-0 flex-1 items-start gap-3 text-left"
          >
            <div
              className={clsx(
                'flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] text-sm font-bold ring-1 ring-inset',
                avatarTone(`${client.name}${client.lastname}${client.id}`)
              )}
            >
              {getInitials(client.name, client.lastname)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <p className="truncate text-[16px] font-semibold leading-snug text-slate-900">
                  {client.name} {client.lastname}
                </p>
                {status === 'moroso' ? (
                  <span className="shrink-0 rounded-md border border-[#FECDD3] bg-[#FFF1F2] px-2 py-0.5 text-[11px] font-semibold text-[#BE123C]">
                    Moroso
                  </span>
                ) : (
                  <ClientStatusBadge status={status} className="shrink-0 !px-2 !py-0.5 !text-[11px]" />
                )}
              </div>
              <p className="mt-0.5 truncate text-xs font-medium text-primary-600">{planName(client)}</p>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                <span className="inline-flex h-7 items-center rounded-lg bg-slate-50 px-2 font-mono text-[11px] font-medium tabular-nums text-slate-700 ring-1 ring-[#E5EAF1]">
                  {cedulaDisplay}
                </span>
                {gremio ? (
                  <span className="inline-flex h-7 items-center rounded-lg bg-slate-100 px-2 text-[11px] font-semibold text-slate-700">
                    {gremio}
                  </span>
                ) : null}
                <span className="inline-flex h-7 items-center gap-1 rounded-lg bg-slate-50 px-2 text-[11px] font-semibold text-slate-700 ring-1 ring-[#E5EAF1]">
                  <UsersIcon className="h-3.5 w-3.5 text-slate-400" aria-hidden />
                  {famCount}
                </span>
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(true);
            }}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 active:scale-95"
            aria-label="Más acciones"
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </button>
        </div>
      </article>

      <BottomSheet open={menuOpen} onClose={() => setMenuOpen(false)} title="Acciones">
        <div className="space-y-0.5 pb-2">
          <SheetAction icon={EyeIcon} label="Ver cliente" onClick={() => run(onOpenDetail)} />
          <SheetAction icon={PencilIcon} label="Editar" onClick={() => run(handlers.onEdit)} />
          <SheetAction
            icon={UserGroupIcon}
            label="Administrar familiares"
            onClick={() => run(handlers.onViewFamily)}
          />
          <SheetAction
            icon={DocumentTextIcon}
            label="Administrar plan"
            onClick={() => run(handlers.onViewInvoices)}
          />
          <SheetAction
            icon={ClockIcon}
            label="Historial"
            onClick={() => run(handlers.onViewHistory)}
          />
          <SheetAction
            icon={BanknotesIcon}
            label="Más acciones · Pago"
            onClick={() => run(handlers.onRegisterPayment)}
          />
          {handlers.onActivateService ? (
            <SheetAction
              icon={BoltIcon}
              label="Activar servicio"
              onClick={() => run(handlers.onActivateService!)}
            />
          ) : null}
          {handlers.onDelete ? (
            <>
              <div className="my-2 border-t border-slate-100" />
              <SheetAction
                icon={TrashIcon}
                label="Eliminar"
                danger
                onClick={() => run(handlers.onDelete)}
              />
            </>
          ) : null}
        </div>
      </BottomSheet>
    </>
  );
};

const SheetAction: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  danger?: boolean;
}> = ({ icon: Icon, label, onClick, danger }) => (
  <button
    type="button"
    onClick={onClick}
    className={clsx(
      'flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium transition active:scale-[0.99]',
      danger ? 'text-rose-600 hover:bg-rose-50' : 'text-slate-800 hover:bg-slate-50'
    )}
  >
    <Icon className={clsx('h-5 w-5 shrink-0', danger ? 'text-rose-500' : 'text-slate-400')} />
    {label}
  </button>
);

export default ClientMobileCard;
